import React, { useCallback, useContext, useState } from 'react';
import {
  ConnectorOverviewUIStateOf,
  UseConnectorOverviewUIReturn,
} from './model';

const createContextTriggerSkeletons = () =>
  React.createContext<ConnectorOverviewUIStateOf>([
    () => ({ connectorComponent: undefined, data: undefined }),
    () => {},
    () => {},
  ]);

const contextTriggerSkeletons = createContextTriggerSkeletons();

const createResource = (
  oldState: ConnectorOverviewUIStateOf[0],
  futureState: UseConnectorOverviewUIReturn[0],
  resolved = false
): ConnectorOverviewUIStateOf[0] => {
  let promise: null | Promise<void> = null;
  let isResolved = resolved;

  return () => {
    if (!promise && !isResolved) {
      promise = new Promise((resolve) => {
        setTimeout(() => {
          resolve(undefined);
        }, 500);
      }).then(() => {
        isResolved = true;
      });
    }

    if (isResolved) {
      return {
        ...oldState(),
        ...futureState,
        data: { ...oldState().data, ...futureState.data },
      };
    }

    throw promise;
  };
};

const ConnectorOverviewUITriggerSkeletonsProvider = ({ children }) => {
  const [value, setValue] = useState<ConnectorOverviewUIStateOf[0]>(
    () => () => ({})
  );

  const handleUpdateWithTrigger = useCallback(
    (data) => {
      setValue(() => createResource(value, data));
    },
    [value]
  );

  const handleUpdateStandardBehavior = useCallback(
    (data) => {
      setValue(() => createResource(value, data, true));
    },
    [value]
  );

  return (
    <contextTriggerSkeletons.Provider
      value={[value, handleUpdateWithTrigger, handleUpdateStandardBehavior]}
    >
      {children}
    </contextTriggerSkeletons.Provider>
  );
};

export const ConnectorOverviewUIProvider = ({ children }) => {
  return (
    <ConnectorOverviewUITriggerSkeletonsProvider>
      {children}
    </ConnectorOverviewUITriggerSkeletonsProvider>
  );
};

export const useConnectorOverviewUI = (): UseConnectorOverviewUIReturn => {
  const [state, setStateTriggerSkeleton, setStateStandardBehavior] = useContext(
    contextTriggerSkeletons
  );

  return [state(), setStateTriggerSkeleton, setStateStandardBehavior];
};
